import {
  type CodigoDePapel,
  PAPEIS,
  PAPEIS_INTERNOS,
  STATUS_DE_USUARIO,
  type StatusDoUsuario,
} from '@locaveri/shared'
import type { ColumnDef } from '@tanstack/react-table'
import { EllipsisVertical, MailPlus, Pencil, Search, UserPlus, Users } from 'lucide-react'
import { type ReactElement, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { Paginacao } from '@/components/data/paginacao'
import { TabelaDeDados } from '@/components/data/tabela-de-dados'
import { EmptyState } from '@/components/state/empty-state'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import {
  type Usuario,
  useAtualizarUsuario,
  useReenviarConvite,
  useUsuarios,
} from '@/features/usuarios/api'
import { BadgeDe2fa, BadgeDeStatusDoUsuario, ListaDePapeis } from '@/features/usuarios/badges'
import { DialogoDeUsuario } from '@/features/usuarios/dialogo-de-usuario'
import { errorMessage } from '@/lib/api'
import { formatarDataHora, SEM_VALOR } from '@/lib/datas'
import { useValorAtrasado } from '@/lib/hooks'
import { formatarTelefoneDeExibicao } from '@/lib/mascaras'
import { usePermissao } from '@/lib/permissoes'

/**
 * Equipe interna: quem entra no painel.
 *
 * A tela é gated por `user.read` na rota; dentro dela, cada ação também pergunta
 * pela sua permissão (`user.create`, `user.update`). O servidor decide de novo —
 * isto aqui é para não oferecer caminho que termina em 403.
 */

const TAMANHO_DA_PAGINA = 20

const ROTULO_DE_STATUS: Readonly<Record<StatusDoUsuario, string>> = {
  ATIVO: 'Ativo',
  PENDENTE: 'Pendente',
  BLOQUEADO: 'Bloqueado',
}

export function UsuariosPage(): ReactElement {
  const podeCriar = usePermissao('user.create')
  const podeEditar = usePermissao('user.update')

  const [pagina, setPagina] = useState(1)
  const [buscaDigitada, setBuscaDigitada] = useState('')
  const [status, setStatus] = useState<StatusDoUsuario | ''>('')
  const [papel, setPapel] = useState<CodigoDePapel | ''>('')
  const [dialogoAberto, setDialogoAberto] = useState(false)
  const [emEdicao, setEmEdicao] = useState<Usuario | undefined>(undefined)

  const busca = useValorAtrasado(buscaDigitada)

  const filtros = useMemo(
    () => ({
      pagina,
      tamanho: TAMANHO_DA_PAGINA,
      busca,
      status,
      papel,
      apenasInternos: true,
    }),
    [pagina, busca, status, papel],
  )

  const consulta = useUsuarios(filtros)
  const atualizar = useAtualizarUsuario()
  const reenviarConvite = useReenviarConvite()

  const temFiltro = busca.length > 0 || status !== '' || papel !== ''

  function abrirCriacao(): void {
    setEmEdicao(undefined)
    setDialogoAberto(true)
  }

  function abrirEdicao(usuario: Usuario): void {
    setEmEdicao(usuario)
    setDialogoAberto(true)
  }

  function alternarSituacao(usuario: Usuario): void {
    const novoStatus: StatusDoUsuario = usuario.status === 'BLOQUEADO' ? 'ATIVO' : 'BLOQUEADO'

    atualizar.mutate(
      { id: usuario.id, dados: { status: novoStatus } },
      {
        onSuccess: () => {
          toast.success(
            novoStatus === 'BLOQUEADO'
              ? `${usuario.nome} não consegue mais entrar no painel.`
              : `${usuario.nome} voltou a ter acesso.`,
          )
        },
        onError: (causa: unknown) => {
          toast.error('Não foi possível alterar a situação', {
            description: errorMessage(causa),
          })
        },
      },
    )
  }

  function convidarNovamente(usuario: Usuario): void {
    reenviarConvite.mutate(usuario.id, {
      onSuccess: () => {
        toast.success('Convite reenviado', { description: `Enviamos para ${usuario.email}.` })
      },
      onError: (causa: unknown) => {
        toast.error('Não foi possível reenviar o convite', { description: errorMessage(causa) })
      },
    })
  }

  const colunas = useMemo<ColumnDef<Usuario>[]>(
    () => [
      {
        id: 'nome',
        header: 'Usuário',
        accessorFn: (linha) => linha.nome,
        cell: ({ row }) => (
          <div className="min-w-0">
            <p className="truncate font-medium text-foreground">{row.original.nome}</p>
            <p className="truncate text-xs text-fg-secondary">{row.original.email}</p>
          </div>
        ),
      },
      {
        id: 'papeis',
        header: 'Papéis',
        enableSorting: false,
        cell: ({ row }) => <ListaDePapeis papeis={row.original.papeis} />,
      },
      {
        id: 'status',
        header: 'Situação',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex flex-wrap items-center gap-1.5">
            <BadgeDeStatusDoUsuario status={row.original.status} />
            <BadgeDe2fa ativo={row.original.totpAtivo} />
            {row.original.emailVerificado ? null : (
              <Badge variant="neutral" title="O e-mail ainda não foi confirmado">
                E-mail não verificado
              </Badge>
            )}
          </div>
        ),
      },
      {
        id: 'telefone',
        header: 'Telefone',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-fg-secondary">
            {formatarTelefoneDeExibicao(row.original.telefone)}
          </span>
        ),
      },
      {
        id: 'ultimoLoginEm',
        header: 'Último acesso',
        accessorFn: (linha) => linha.ultimoLoginEm ?? '',
        cell: ({ row }) => (
          <span className="whitespace-nowrap tabular-money text-fg-secondary">
            {formatarDataHora(row.original.ultimoLoginEm)}
          </span>
        ),
      },
      {
        id: 'acoes',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          if (!podeEditar) {
            return <span className="text-xs text-fg-muted">{SEM_VALOR}</span>
          }

          const usuario: Usuario = row.original

          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Ações de ${usuario.nome}`}
                  className="size-8"
                >
                  <EllipsisVertical aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem
                  onSelect={() => {
                    abrirEdicao(usuario)
                  }}
                >
                  <Pencil aria-hidden="true" />
                  Editar
                </DropdownMenuItem>

                <DropdownMenuItem
                  onSelect={() => {
                    convidarNovamente(usuario)
                  }}
                >
                  <MailPlus aria-hidden="true" />
                  Reenviar convite
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                <DropdownMenuItem
                  onSelect={() => {
                    alternarSituacao(usuario)
                  }}
                >
                  {usuario.status === 'BLOQUEADO' ? 'Reativar acesso' : 'Bloquear acesso'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )
        },
      },
    ],
    // As funções de ação são estáveis dentro do render desta tela; o que muda o
    // conjunto de colunas é a permissão de edição.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [podeEditar],
  )

  const dados = consulta.data

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Usuários internos</h1>
          <p className="text-sm text-fg-secondary">
            Quem tem acesso ao painel da operação, com os papéis de cada um.
          </p>
        </div>

        {podeCriar ? (
          <Button onClick={abrirCriacao}>
            <UserPlus aria-hidden="true" />
            Novo usuário
          </Button>
        ) : null}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="filtro-busca">Buscar</Label>
          <div className="relative">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2
                text-fg-muted"
            />
            <Input
              id="filtro-busca"
              type="search"
              value={buscaDigitada}
              onChange={(evento) => {
                setPagina(1)
                setBuscaDigitada(evento.target.value)
              }}
              placeholder="Nome ou e-mail"
              className="pl-9"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="filtro-status">Situação</Label>
          <Select
            id="filtro-status"
            value={status}
            onChange={(evento) => {
              setPagina(1)
              const valor = evento.target.value
              setStatus(STATUS_DE_USUARIO.find((item) => item === valor) ?? '')
            }}
          >
            <option value="">Todas</option>
            {STATUS_DE_USUARIO.map((item) => (
              <option key={item} value={item}>
                {ROTULO_DE_STATUS[item]}
              </option>
            ))}
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="filtro-papel">Papel</Label>
          <Select
            id="filtro-papel"
            value={papel}
            onChange={(evento) => {
              setPagina(1)
              const valor = evento.target.value
              setPapel(PAPEIS_INTERNOS.find((item) => item === valor) ?? '')
            }}
          >
            <option value="">Todos</option>
            {PAPEIS_INTERNOS.map((item) => (
              <option key={item} value={item}>
                {PAPEIS[item].nome}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <TabelaDeDados
        colunas={colunas}
        dados={dados?.itens ?? []}
        carregando={consulta.isPending}
        erro={consulta.isError ? consulta.error : undefined}
        aoTentarNovamente={() => {
          void consulta.refetch()
        }}
        legenda="Usuários internos da Locaveri"
        estadoVazio={
          <EmptyState
            icone={Users}
            titulo={temFiltro ? 'Nenhum usuário com esses filtros' : 'Nenhum usuário cadastrado'}
            descricao={
              temFiltro
                ? 'Ajuste a busca, a situação ou o papel para ver outros resultados.'
                : 'Cadastre a equipe que vai operar o painel. Cada pessoa define a própria senha pelo convite.'
            }
            acao={
              temFiltro ? (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setBuscaDigitada('')
                    setStatus('')
                    setPapel('')
                    setPagina(1)
                  }}
                >
                  Limpar filtros
                </Button>
              ) : podeCriar ? (
                <Button onClick={abrirCriacao}>
                  <UserPlus aria-hidden="true" />
                  Novo usuário
                </Button>
              ) : undefined
            }
          />
        }
      />

      <Paginacao
        pagina={dados?.pagina ?? pagina}
        tamanho={dados?.tamanho ?? TAMANHO_DA_PAGINA}
        total={dados?.total ?? 0}
        aoMudarDePagina={setPagina}
        desabilitado={consulta.isFetching}
      />

      <DialogoDeUsuario
        aberto={dialogoAberto}
        usuario={emEdicao}
        aoFechar={() => {
          setDialogoAberto(false)
        }}
      />
    </div>
  )
}
